import styled from '@emotion/styled'
import { FunctionComponent, useMemo } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import Button from '~/components/Button'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { PageContent } from '~/components/PageContent'
import { TabProp, Tabs } from '~/components/Tabs'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { SitePageHelpFlyout } from './SitePageHelpFlyout'
import { PbsSitePageContext, usePbsSitePageContext } from './useSitePageContext'

const ContentWrapper = styled.div`
  margin-top: 20px;
`

// Constants
const FLYOUT_WIDTH = '600px'

// Tracking labels
const TRACKING_LABEL = 'pbs-sites-and-stacks'

type TAB_LABEL = 'Configuration' | 'Activation' | 'Stacks' | 'Connect to Pubstack'
const TAB_URLS: { [key in TAB_LABEL]: string } = {
  Configuration: 'configuration',
  Activation: 'activation',
  Stacks: 'stacks',
  'Connect to Pubstack': 'connect-to-pubstack',
}

export const SitePage: FunctionComponent = () => {
  const { siteId } = useParams()
  const navigate = useNavigate()
  const flyout = useGlobalFlyout()
  const location = useLocation()
  const user = useAuthenticatedUser()

  const { scopeId, site, adUnits, bidderRules, isLoading, stacks, product } = usePbsSitePageContext(siteId)
  const breadcrumbs = useBreadcrumbs(site)
  const hasAnalyticsFeature = user.hasFeature('analytics')

  const tabs: TabProp[] = useMemo(
    () => [
      // Activation first, because it is where the page opens: the route with no tab in it redirects here,
      // and a first tab that is not the landing one reads as a bug the moment anyone compares the two.
      {
        label: 'Activation',
        active: /activation\/?$/.test(location?.pathname ?? ''),
        trackingLabel: `${TRACKING_LABEL}-activation-tab-apply`,
      },
      // What the media *is* — its name and its Prebid variable. No SPA here: that is One's own
      // page-detection setting, on One's own endpoint, and a Node media has none to configure.
      {
        label: 'Configuration',
        active: /configuration\/?$/.test(location?.pathname ?? ''),
        trackingLabel: `${TRACKING_LABEL}-configuration-tab-apply`,
      },
      {
        label: 'Stacks',
        active: /stacks\/?$/.test(location?.pathname ?? ''),
        trackingLabel: `${TRACKING_LABEL}-stacks-tab-apply`,
        disabled: isLoading || stacks.length === 0,
      },
      {
        label: 'Connect to Pubstack',
        active: /connect-to-pubstack\/?$/.test(location?.pathname ?? ''),
        disabled: (!site || site.pbsStandaloneStatus === 'inactive') && !hasAnalyticsFeature,
        trackingLabel: `${TRACKING_LABEL}-connect-tab-apply`,
      },
    ],
    [location, site, stacks, isLoading, hasAnalyticsFeature]
  )

  const onTabChange = (tab: TabProp) => {
    navigate(TAB_URLS[tab.label as TAB_LABEL])
  }

  const handleHelpClick = () =>
    flyout.open(
      SitePageHelpFlyout,
      { color: Colors.Kaiminus, width: FLYOUT_WIDTH, closeTrackingLabel: `${TRACKING_LABEL}-help-close` },
      {
        siteEnv: site?.env,
        scopeId,
        onClose: () => flyout.close(),
      }
    )

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <Button iconName="help" variant="tertiary" onClick={handleHelpClick} trackingLabel={`${TRACKING_LABEL}-help`}>
          Help
        </Button>
      }
    >
      <Tabs tabs={tabs} onClick={onTabChange} />
      {!isLoading ? (
        <ContentWrapper>
          <Outlet
            context={
              {
                site,
                scopeId,
                adUnits,
                stacks,
                bidderRules,
                isLoading,
                product,
              } satisfies PbsSitePageContext
            }
          />
        </ContentWrapper>
      ) : (
        <></>
      )}
    </PageContent>
  )
}
